// The two definitive local-guide hubs — written in the hosts' voice (Bill &
// Jonathan operate the rentals and live the neighborhood). Sections support:
//   h: heading · paragraphs: string[] · list: string[] · pick: host callout ·
//   links: [{ label, to }] internal links.
// Content is drafted from local knowledge and reviewed by the owners before
// publishing. Keep claims accurate; hedge anything seasonal or changeable.

export const guides = [
  {
    slug: "wilton-manors-guide",
    shortTitle: "Wilton Manors Guide",
    eyebrow: "Wilton Manors · Our hometown",
    h1: "The Double Rainbow Guide to Wilton Manors",
    metaTitle: "Wilton Manors Guide — Best Restaurants, Gay Bars, Brunch & Things to Do | Double Rainbow Suites",
    metaDescription:
      "The definitive local guide to Wilton Manors from hosts who live and host here: best restaurants, gay bars, brunch, coffee, grocery stores, parking, walking distances, bike routes, Pride and Wilton Drive tips.",
    answer:
      "Wilton Manors is a one-square-mile island city in the middle of Fort Lauderdale — and the LGBTQ+ capital of South Florida. Nearly everything happens on Wilton Drive, a walkable strip of restaurants, gay bars, cafés and galleries. We operate vacation rentals a block or two from the Drive, and this is the guide we send every guest: where to eat, drink, brunch, park and wander.",
    sections: [
      {
        id: "wilton-drive",
        h: "Wilton Drive 101",
        paragraphs: [
          "Wilton Drive is the town's main street — roughly a mile of bars, restaurants, cafés and shops running through the Island City. It is one of the most walkable, welcoming LGBTQ+ strips in the country: rainbow flags overhead, sidewalk tables, and a crowd that mixes locals and visitors every night of the week.",
          "The Drive is busiest Thursday through Sunday. Dinner runs 6–10pm, bars peak after 10pm, and Sunday belongs to brunch and a classic afternoon tea-dance at Hunters or Scandals. Nearly everything in this guide sits on or within a block of the Drive.",
        ],
      },
      {
        id: "best-restaurants",
        h: "The best restaurants",
        paragraphs: [
          "For a town this small, the eating is exceptional. Rosie's Bar & Grill is the loud, joyful institution — burgers, sandwiches and a garden patio that's always a party. Ethos Greek Bistro does refined Greek plates, Tulio's Tacos & Tequila Bar is the go-to for Mexican and margaritas, and Union is the date-night pick.",
          "Also on our list: Proof for tapas, shareable plates and a standout natural-wine list, Apt 9F for cocktails and elevated American plates, Tropics Grille for the classic supper-club evening, and Inika for vegetarian done properly.",
        ],
        pick: "Sunday at Rosie's — come for lunch, stay for the singalong patio. Arrive before noon or be ready to wait (it's worth it).",
      },
      {
        id: "best-gay-bars",
        h: "The best gay bars & nightlife",
        paragraphs: [
          "Hunters Nightclub is the anchor — a big dance floor, nightly DJs and the see-and-be-seen video bar. Georgie's Alibi Monkey Bar is the sprawling, friendly classic with a legendary happy hour. The Eagle brings the leather crowd, Gym Sports Bar covers game day, and The Pub keeps the karaoke and drag rolling.",
          "For cocktails without the club volume, try Drynk or No Manors — stylish lounges a short stroll apart — and LIT Bar for a late one. Every one of these is on or just off Wilton Drive, and all are a short walk from our suites.",
        ],
        pick: "The perfect Drive crawl: happy hour at Georgie's, dancing at Hunters, a nightcap at Drynk, and some late night excitement at The Eagle — all within a few blocks, no car required.",
      },
      {
        id: "best-brunch",
        h: "The best brunch",
        paragraphs: [
          "Brunch is a competitive sport in Wilton Manors. The weekend drag brunches are the headline — Rosie's and Georgie's Alibi both pack out for bottomless mimosas and show-stopping queens. Book ahead or arrive early; walk-ins after 11am will wait.",
          "For a slower morning, Stork's Bakery & Café does excellent pastries, quiche and coffee on a sunny corner patio, and Ethos serves a lovely Greek-style weekend brunch.",
        ],
      },
      {
        id: "best-coffee",
        h: "The best coffee",
        paragraphs: [
          "Stork's Bakery & Café is the morning institution — proper espresso, fresh-baked everything, and a patio full of regulars walking their dogs. It's our first stop most days and an easy stroll from the suites.",
          "For grab-and-go before the beach, the cafés along the Drive open early, and Jazzberry does a great iced coffee alongside its ice cream. Working remotely for the morning? Stork's patio is the unofficial town office.",
        ],
        pick: "Stork's, obviously. Flat white, a table on the patio, and ten minutes of the best people-watching in town.",
      },
      {
        id: "grocery-stores",
        h: "Grocery stores & essentials",
        paragraphs: [
          "Publix sits right by Wilton Drive — you can walk your groceries home from our suites in minutes. CVS is equally close for pharmacy basics, and Walgreens is just up the road.",
          "For a bigger shop, there's a larger Publix and a Target a short drive north in Oakland Park, and Total Wine on Federal Highway for stocking the fridge properly.",
        ],
      },
      {
        id: "stonewall-museum",
        h: "Stonewall National Museum, Archives & Library",
        paragraphs: [
          "One of the largest LGBTQ+ archives and libraries in the United States sits right here. The Stonewall National Museum hosts thoughtful rotating exhibitions, talks and film screenings, and its archive holds decades of queer history.",
          "It's free to visit (donations welcome), air-conditioned, and a genuinely moving hour — our favorite rainy-afternoon stop in town.",
        ],
      },
      {
        id: "art-walk",
        h: "Wilton Manors Art Walk",
        paragraphs: [
          "Once a month, Wilton Drive turns into an open-air gallery night — local artists, pop-up stalls, live music and shops open late. It's free, friendly and very local: the evening when the whole town seems to be out strolling.",
          "Dates move with the season, so check the calendar before you come — or just ask us, we keep it updated.",
        ],
        links: [{ label: "See what's on this month", to: "/events" }],
      },
      {
        id: "pride",
        h: "Pride in Wilton Manors",
        paragraphs: [
          "Stonewall Pride in June is the big one — the parade and street festival take over the Drive, and the nighttime GLOW Parade is unforgettable. Wicked Manors on Halloween is the year's second giant street party — the costumes alone are worth the trip.",
          "But honestly, Pride is year-round here: flags on every storefront, welcoming crowds, and the friendliest nightlife in Florida.",
        ],
        links: [{ label: "Stonewall Pride visitor guide", to: "/events/stonewall-pride-wilton-manors" }],
      },
      {
        id: "parking",
        h: "Parking in Wilton Manors",
        paragraphs: [
          "Good news first: parking at every Double Rainbow Suites home is free and off-street. Around town, street parking on the Drive is metered (the PayByPhone app is easiest), and there are municipal lots on the side streets off NE 26th Street.",
          "On festival weekends — Pride, Halloween, the art walks — the lots fill by mid-morning. Staying within walking distance removes the problem entirely.",
        ],
      },
      {
        id: "walking-distances",
        h: "Walking distances from our suites",
        paragraphs: [
          "Times below are from 1425 NE 24th St, where our Wilton Manors homes sit. The routes are flat, sidewalk-lined and feel safe day and night — comfortable shoes recommended, heels optional.",
        ],
        list: [
          "Wilton Drive (north end) — about 3 minutes on foot",
          "Rosie's Bar & Grill — about 5 minutes",
          "Stork's Bakery & Café — about 6 minutes",
          "Hunters Nightclub — about 6 minutes",
          "Georgie's Alibi Monkey Bar — about 7 minutes",
          "Publix & CVS — about 5 minutes",
          "Stonewall National Museum — about 8 minutes",
        ],
      },
      {
        id: "bike-routes",
        h: "Bike routes",
        paragraphs: [
          "Every stay includes free beach-cruiser bikes, and Wilton Manors is made for them — flat, quiet, palm-shaded streets. The classic ride: east through the residential lanes to Fort Lauderdale Beach, about 2.5 miles of easy cruising.",
          "Shorter loops we love: around the Middle River canals at golden hour, and down to Richardson Historic Park & Nature Preserve for a shaded waterfront wander. Bike racks sit outside most bars and cafés on the Drive.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is Wilton Manors walkable?",
        a: "Very — it's a one-square-mile town where restaurants, bars, coffee and groceries cluster on and around Wilton Drive. Our suites are a short walk from all of it.",
      },
      {
        q: "Is Wilton Manors safe?",
        a: "It's known as one of South Florida's safest, most welcoming communities, with a lively neighborhood feel day and night — one reason we chose to live and host here.",
      },
      {
        q: "Where is the gay area of Fort Lauderdale?",
        a: "Wilton Manors — specifically Wilton Drive — is the heart of LGBTQ+ life in Greater Fort Lauderdale, with most of the area's gay bars and clubs within a few walkable blocks.",
      },
      {
        q: "What's the best time to visit Wilton Manors?",
        a: "November through April for perfect weather, June for Stonewall Pride, and Halloween for Wicked Manors. Summer is hot but quieter and great value.",
      },
    ],
    otherHub: { label: "Read the Fort Lauderdale guide", to: "/fort-lauderdale-guide" },
  },
  {
    slug: "fort-lauderdale-guide",
    shortTitle: "Fort Lauderdale Guide",
    eyebrow: "Fort Lauderdale · Our playground",
    h1: "The Double Rainbow Guide to Fort Lauderdale",
    metaTitle: "Fort Lauderdale Guide — Best Beaches, Las Olas, Riverwalk & Things to Do | Double Rainbow Suites",
    metaDescription:
      "A local host's guide to Fort Lauderdale: the best beaches (including Sebastian Beach), Las Olas, Riverwalk, Flagler Village, the cruise port, FLL airport, rainy-day ideas and pet-friendly things to do.",
    answer:
      "Fort Lauderdale is a beach city threaded with hundreds of miles of canals, where the sand, the boating life and a growing arts scene sit minutes apart. We host guests here year-round, and this is the guide we wish every visitor arrived with: which beach for which mood, where to eat and wander, how to get around, and what to do when it rains.",
    sections: [
      {
        id: "best-beaches",
        h: "The best beaches",
        paragraphs: [
          "Fort Lauderdale Beach is the postcard: a wide, palm-lined promenade with famously clear water, about 10 minutes from our homes. The sweetest swimming and people-watching run from Las Olas Boulevard north to Sunrise.",
          "Want quieter? Lauderdale-By-The-Sea is a charming low-rise beach village about 15 minutes north, and Pompano Beach Pier has a beautiful new pier and restaurants. All are an easy drive — and every stay includes free beach-cruiser bikes.",
        ],
        links: [{ label: "Rentals near the beach", to: "/fort-lauderdale-vacation-rentals-near-beach" }],
      },
      {
        id: "sebastian-beach",
        h: "Sebastian Beach",
        paragraphs: [
          "Sebastian Street Beach — where Sebastian Street meets A1A — is Fort Lauderdale's famous gay beach: friendly, social and steps from beachfront bars. It's about 10 minutes from our Wilton Manors suites.",
          "Go late morning for a good spot, and stay for sunset — the crowd drifts to the nearby patios as the light goes golden.",
        ],
        pick: "Sebastian on a Saturday, then happy hour back on the Drive. That's the classic Double Rainbow day.",
      },
      {
        id: "las-olas",
        h: "Las Olas Boulevard",
        paragraphs: [
          "Las Olas is the city's elegant main street — galleries, boutiques and sidewalk restaurants running from downtown all the way to the beach. Come for a long lunch, stay for a wander, and walk it end to end at dusk when the lights come on.",
          "Our routine: coffee and a stroll at the beach end, browse the galleries mid-boulevard, then dinner downtown. It's about 10 minutes by car from our homes — or make a day of it on the Water Taxi.",
        ],
      },
      {
        id: "riverwalk",
        h: "Riverwalk & the New River",
        paragraphs: [
          "The Riverwalk follows the New River through downtown — a shaded promenade past yachts, museums and waterfront bars. Ride the Water Taxi from here for the best-value sightseeing in Florida: mansions, megayachts and a running commentary of local gossip.",
          "History stop: the Stranahan House, the city's oldest surviving home, sits right on the river. Then cocktails on a riverfront patio as the boats drift by.",
        ],
      },
      {
        id: "flagler-village",
        h: "Flagler Village",
        paragraphs: [
          "Flagler Village is Fort Lauderdale's arts district — warehouses turned galleries, breweries and coffee roasters just north of downtown. The monthly art walk fills the streets with open studios, food trucks and live music.",
          "Beer lovers, this is your neighborhood: Funky Buddha's famous brewery is just up the road in Oakland Park, and smaller taprooms cluster around the village.",
        ],
      },
      {
        id: "cruise-port",
        h: "The cruise port — Port Everglades",
        paragraphs: [
          "Port Everglades is one of the busiest cruise ports on earth — and only about 15 minutes from our homes. If you're sailing, arriving a day early (or staying a day after) turns a travel day into a beach day.",
          "Every major line is here: Royal Caribbean (usually Terminal 18), Celebrity (Terminal 25), Princess (Terminal 2), Holland America (Terminal 26), Disney (Terminal 4) and Carnival (Terminals 19 & 21). Terminals can change by sailing, so check your cruise documents.",
        ],
        links: [
          { label: "Where to stay before or after a cruise", to: "/fort-lauderdale-cruise-port-hotels" },
          { label: "Rentals near the cruise port", to: "/fort-lauderdale-vacation-rentals-near-cruise-port" },
          { label: "Read the full Port Everglades cruise guide", to: "/port-everglades-cruise-guide" },
        ],
      },
      {
        id: "fll-airport",
        h: "FLL airport",
        paragraphs: [
          "Fort Lauderdale–Hollywood International (FLL) is one of the easiest big airports in America — compact, quick, and about 15–20 minutes from our homes. Rideshare queues are short and there's flat-rate taxi service to the beach and port.",
          "Flying into Miami instead? It's about 45 minutes away; the Brightline train from Miami to downtown Fort Lauderdale is the smoothest option — and a fun ride.",
        ],
      },
      {
        id: "things-to-do",
        h: "Things to do",
        paragraphs: [
          "Beyond the beach: Hugh Taylor Birch State Park is a lagoon-side oasis for kayaking and shaded trails right off A1A, and the Bonnet House is a dreamy 1920s artist's estate hidden in the trees. The Water Taxi doubles as the best-value harbour tour anywhere.",
          "For a bigger day out, Everglades airboat tours start about 30 minutes west, Miami's South Beach is 40 minutes south, and Key West makes a long but glorious day — about four hours each way.",
        ],
      },
      {
        id: "rainy-day",
        h: "Rainy-day activities",
        paragraphs: [
          "Summer afternoons bring short, dramatic storms — plan around them. The NSU Art Museum downtown is excellent, the Museum of Discovery & Science (with its giant-screen theater) is a family winner, and the Stonewall National Museum on Wilton Drive is our favorite hour in town.",
          "Or lean in: covered shopping at the Galleria, a long lunch on Las Olas, then back to your suite to watch the storm roll through — they're usually gone by happy hour.",
        ],
      },
      {
        id: "pet-friendly",
        h: "Pet-friendly Fort Lauderdale",
        paragraphs: [
          "Fort Lauderdale loves dogs. There's a designated canine stretch of Fort Lauderdale Beach (check the city's current permit rules before you go), and Snyder Park's dog park even has a swim lake for dogs.",
          "Closer to home, the patios along Wilton Drive welcome dogs almost universally — and all four of our homes are pet-friendly. Just add your pet when you book.",
        ],
        links: [{ label: "Pet-friendly rentals", to: "/fort-lauderdale-pet-friendly-vacation-rentals" }],
      },
    ],
    faqs: [
      {
        q: "What is the best beach in Fort Lauderdale?",
        a: "Fort Lauderdale Beach between Las Olas and Sunrise for the full promenade experience; Sebastian Street Beach for the LGBTQ+ scene; Lauderdale-By-The-Sea for a quieter village feel. All are 10–15 minutes from our homes.",
      },
      {
        q: "How far is Fort Lauderdale airport from the beach?",
        a: "About 15 minutes by car — FLL is one of the closest major airports to a beach in the country.",
      },
      {
        q: "What is there to do in Fort Lauderdale when it rains?",
        a: "The NSU Art Museum, the Museum of Discovery & Science, the Stonewall National Museum on Wilton Drive, the Galleria — or a long lunch on Las Olas. Storms here are usually short.",
      },
      {
        q: "Is Fort Lauderdale good for a pre-cruise stay?",
        a: "Perfect — Port Everglades is about 15 minutes from our homes, and a day or two either side of your sailing is an easy mini-vacation.",
      },
    ],
    otherHub: { label: "Read the Wilton Manors guide", to: "/wilton-manors-guide" },
  },
  {
    slug: "port-everglades-cruise-guide",
    shortTitle: "Port Everglades Cruise Guide",
    eyebrow: "Port Everglades · Cruise like a local",
    h1: "The Double Rainbow Port Everglades Cruise Guide",
    metaTitle: "Port Everglades Cruise Guide — Terminals, Cruise Lines & Where to Stay | Double Rainbow Suites",
    metaDescription:
      "The local guide to cruising from Port Everglades: which cruise lines sail and their terminals, how to get to the port, what to do the night before, and where to stay — from hosts 15 minutes away.",
    answer:
      "Port Everglades is one of the three busiest cruise ports in the world, and one of the easiest to cruise from — barely ten minutes from the airport, with a usual terminal for each cruise line. We host cruise guests year-round at our homes about 15 minutes from the port, and this is the guide we send them: which line sails from which terminal, how to get there, and how to turn the night before into part of the vacation.",
    sections: [
      {
        id: "cruise-lines",
        h: "Which line, which terminal",
        paragraphs: [
          "Each cruise line at Port Everglades has its usual terminal, and your ship's terminal is printed on your cruise documents — check there first, because assignments can change by sailing. As of the 2026 season, this is the layout:",
        ],
        list: [
          "Royal Caribbean — usually Terminal 18 (the Oasis-class giants)",
          "Celebrity Cruises — Terminal 25, the port's glass-walled premium terminal",
          "Princess Cruises — Terminal 2",
          "Holland America Line — Terminal 26",
          "Disney Cruise Line — Terminal 4, a dedicated family terminal",
          "Carnival Cruise Line — usually Terminals 19 & 21",
        ],
      },
      {
        id: "getting-there",
        h: "Getting to the port",
        paragraphs: [
          "The port's entrance is at 1850 Eller Drive, Fort Lauderdale. From our homes it's about a 15-minute rideshare — we recommend booking your ride the night before a morning departure. From FLL airport, the port is barely 10 minutes — one of the closest airport-to-cruise-port pairs in the country.",
          "Driving yourself? Port Everglades has secure parking garages at the port (the Heron and Palm garages serve most terminals), and ride-app drop-offs go straight to your terminal's door. Aim to arrive inside the check-in window on your documents — earlier isn't always better, as terminals board in waves.",
        ],
      },
      {
        id: "night-before",
        h: "The night before, done right",
        paragraphs: [
          "The single best cruise upgrade we know: fly in the day before. Weather delays and tight connections sink more embarkation mornings than anything else — and a relaxed evening in Wilton Manors or on Las Olas beats a 4am alarm at home every time.",
          "Our suggested ritual: check in, dinner on Wilton Drive, an early night in a proper bed, coffee and a pastry at Stork's in the morning, then a 15-minute ride to your terminal. Pack a small day bag with documents, medications and swimwear — checked bags can take a few hours to reach your cabin.",
        ],
        pick: "Tell us your sailing date when you book — we'll flag anything happening that weekend and help you time the port run to the minute.",
      },
      {
        id: "after-you-sail",
        h: "After you sail",
        paragraphs: [
          "Don't rush straight to the airport. Disembarkation usually wraps by mid-morning, and an extra night turns a travel day into a beach day — Sebastian Beach, a slow brunch, one more sunset. If you must fly the same day, book flights from early afternoon onward to keep disembarkation morning stress-free.",
        ],
      },
      {
        id: "where-to-stay",
        h: "Where to stay",
        paragraphs: [
          "All four Double Rainbow Suites homes are about 15 minutes from the port, with free parking, full kitchens and self check-in — pet-friendly, and far more space than a port hotel room. The Private Pool Oasis sleeps six for family sailings; our Wilton Manors king suites are perfect for couples.",
        ],
        links: [
          { label: "Before or after your cruise", to: "/fort-lauderdale-cruise-port-hotels" },
          { label: "Vacation rentals near the cruise port", to: "/fort-lauderdale-vacation-rentals-near-cruise-port" },
        ],
      },
    ],
    faqs: [
      {
        q: "How far is Port Everglades from Fort Lauderdale airport?",
        a: "About 10 minutes by car — roughly 3 miles. It's one of the closest airport-to-cruise-port transfers in the US, which makes flying in the day before your sailing easy.",
      },
      {
        q: "Which terminal does my cruise ship leave from?",
        a: "Your terminal is printed on your cruise documents — Royal Caribbean usually sails from Terminal 18, Celebrity Terminal 25, Princess Terminal 2, Holland America Terminal 26, Disney Terminal 4 and Carnival Terminals 19 & 21 — but assignments can change by sailing, so always check your documents.",
      },
      {
        q: "Where should I stay the night before a Port Everglades cruise?",
        a: "Within a short ride of the port — our pet-friendly homes are about 15 minutes away, with full kitchens, free parking and far more space than a port hotel room. Book direct for the best rate.",
      },
      {
        q: "Is there parking at Port Everglades?",
        a: "Yes — the port operates secure parking garages (the Heron and Palm garages serve most terminals) within walking distance of the ships. Current rates and details are on the port's official site.",
      },
    ],
    otherHub: { label: "Read the Fort Lauderdale guide", to: "/fort-lauderdale-guide" },
    cta: {
      h: "Sailing soon? Stay minutes from the port.",
      body: "Pet-friendly homes with full kitchens, free parking and self check-in — about 15 minutes from every Port Everglades terminal. Book direct for the best rate.",
      label: "See pre & post-cruise stays",
      to: "/fort-lauderdale-cruise-port-hotels",
    },
  },
];

export const getGuide = (slug) => guides.find((g) => g.slug === slug);
